/*
================================================================================
File: src/components/common/Modal.js
Description: Standardized modal component for consistent UI.
================================================================================
*/
import React from 'react';

const Modal = ({ show, onClose, title, children, footer, maxWidth = 'max-w-2xl', icon = null, iconColor = 'blue' }) => {
  if (!show) return null;

  const iconBgColors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
    indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 overflow-y-auto h-full w-full flex items-center justify-center z-50 p-4">
      <div className={`relative w-full ${maxWidth} mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]`}>
        {/* Modal Header */}
        <div className="flex rounded-xl justify-between items-center gap-4 px-6 py-5 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {icon && (
              <div className={`p-2.5 rounded-lg flex-shrink-0 ${iconBgColors[iconColor] || iconBgColors.blue}`}>
                {icon}
              </div>
            )}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-5 dark:text-gray-200 overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {children}
        </div>

        {/* Modal Footer */}
        {footer && (
          <div className="flex justify-end items-center gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 sticky bottom-0 z-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;